import { DesignTokens } from "../../types"

export const createButton: (
  foundation: DesignTokens.Foundation,
  contextual: DesignTokens.Contextual,
) => DesignTokens.Button = (
  { sizes, spaces },
  {
    borderRadii,
    borderWidths,
    borderColors,
    fonts,
    fontSizes,
    fontWeights,
    lineHeights,
    backgroundColors,
    textColors,
  },
) => ({
  base: {
    default: {
      container: {
        minWidth: sizes[40],
        height: sizes[40],
        borderRadius: borderRadii.medium,
        borderWidth: borderWidths.small,
        paddingHorizontal: spaces.spacing16,
        paddingVertical: spaces.spacing8,
        fontFamily: fonts.body,
        fontWeight: fontWeights.semibold,
        fontSize: fontSizes.large,
        lineHeight: lineHeights.large,
        color: textColors.link,
      },
      icon: {
        width: sizes[20],
        height: sizes[20],
        marginHorizontal: spaces.spacing8,
      },
    },
    states: {
      hover: {
        container: {},
        icon: {},
      },
      disabled: {
        container: {
          backgroundColor: backgroundColors.default,
          borderColor: borderColors.disabled,
          color: textColors.disabled,
        },
      },
    },
    sizes: {
      small: {
        container: {
          minWidth: sizes[36],
          height: sizes[36],
          fontSize: fontSizes.medium, // large  -> medium
          lineHeight: lineHeights.medium,
        },
        icon: {
          width: sizes[16],
          height: sizes[16],
        },
      },
      medium: {
        container: {
          paddingVertical: spaces.spacing8,
        },
      },
      large: {
        container: {
          minWidth: sizes[48],
          height: sizes[48],
          paddingVertical: spaces.spacing12,
        },
      },
    },
  },
  variants: {
    default: {
      default: {
        container: {
          backgroundColor: backgroundColors.action,
          borderColor: borderColors.transparent,
          color: textColors.inverse,
        },
      },
      states: {
        hover: {
          container: {
            backgroundColor: backgroundColors.actionHover,
          },
        },
        disabled: {
          container: {
            backgroundColor: backgroundColors.disabled,
          },
        },
      },
      sizes: {},
    },
    cta: {
      default: {
        container: {
          backgroundColor: backgroundColors.brand,
          borderColor: borderColors.transparent,
          color: textColors.inverse,
        },
      },
      states: {
        hover: {
          container: {
            backgroundColor: backgroundColors.brandHover,
          },
        },
        disabled: {
          container: {
            backgroundColor: backgroundColors.disabled,
          },
        },
      },
      sizes: {},
    },
    primary: {
      default: {
        container: {
          backgroundColor: backgroundColors.default,
          borderColor: borderColors.selection,
          color: textColors.link,
        },
      },
      states: {
        hover: {
          container: {
            backgroundColor: backgroundColors.actionHover,
            color: textColors.inverse,
          },
        },
        disabled: {
          container: {
            backgroundColor: backgroundColors.default,
            borderColor: borderColors.disabled,
            color: textColors.disabled,
          },
        },
      },
      sizes: {},
    },
    secondary: {
      default: {
        container: {
          backgroundColor: backgroundColors.default,
          borderColor: borderColors.default,
          color: textColors.default,
        },
      },
      states: {
        hover: {
          container: {
            borderColor: borderColors.hover,
          },
        },
        disabled: {
          container: {
            borderColor: borderColors.default,
            color: textColors.disabled,
          },
        },
      },
      sizes: {},
    },
    inverse: {
      default: {
        container: {
          backgroundColor: backgroundColors.inverse,
          borderColor: borderColors.inverse,
          color: textColors.inverse,
        },
      },
      states: {
        hover: {
          container: {
            backgroundColor: backgroundColors.inverse,
            borderColor: borderColors.hover,
          },
        },
        disabled: {
          container: {
            borderColor: borderColors.disabled,
            backgroundColor: backgroundColors.inverse,
            color: textColors.disabled,
          },
        },
      },
      sizes: {},
    },
  },
})

export const createTextButton: (
  foundation: DesignTokens.Foundation,
  contextual: DesignTokens.Contextual,
) => DesignTokens.TextButton = (
  { sizes, spaces },
  { fonts, fontSizes, fontWeights, lineHeights, textColors },
) => ({
  base: {
    default: {
      container: {
        minWidth: sizes[40],
        height: sizes[40],
        paddingVertical: spaces.spacing8,
        fontFamily: fonts.body,
        fontWeight: fontWeights.semibold,
        fontSize: fontSizes.large,
        lineHeight: lineHeights.large,
        color: textColors.link,
      },
      icon: {
        width: sizes[20],
        height: sizes[20],
        marginHorizontal: spaces.spacing8,
      },
    },
    states: {
      hover: {
        container: {
          textDecorationLine: "underline",
        },
        icon: {},
      },
    },
    sizes: {
      small: {
        container: {
          minWidth: sizes[36],
          height: sizes[36],
          fontSize: fontSizes.medium, // large  -> medium
          lineHeight: lineHeights.medium,
        },
        icon: {
          width: sizes[16],
          height: sizes[16],
        },
      },
      medium: {},
    },
  },
  variants: {
    inverse: {
      default: {
        container: {
          color: textColors.inverse,
        },
      },
      states: {
        hover: {
          container: {},
        },
      },
      sizes: {},
    },
  },
})

export const createIconButton: (
  foundation: DesignTokens.Foundation,
  contextual: DesignTokens.Contextual,
) => DesignTokens.IconButton = (
  { sizes, spaces },
  { borderRadii, borderWidths, borderColors, backgroundColors },
) => ({
  base: {
    default: {
      container: {
        width: sizes[36],
        height: sizes[36],
        borderRadius: borderRadii.medium,
      },
      icon: {
        width: sizes[24],
        height: sizes[24],
      },
    },
    states: {
      hover: {
        container: {
          backgroundColor: backgroundColors.lightOverlay,
        },
        icon: {},
      },
    },
    hasBorder: {
      container: {
        borderWidth: borderWidths.small,
        borderStyle: "solid",
        borderColor: borderColors.default,
      },
    },
  },
  platforms: {
    desktop: {
      default: {
        container: {},
        icon: {
          width: sizes[32],
          height: sizes[32],
        },
      },
      states: {},
      hasBorder: {},
    },
    mobile: {
      default: {},
      states: {},
      hasBorder: {},
    },
  },
})
